import type { FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'

import { motion } from 'framer-motion'
import { cn } from '@/lib/utils'

import {
    BadgeCheck,
    Building2,
    CircuitBoard,
    Cloud,
    Cog,
    Cpu,
    Landmark,
    Rocket,
    Zap
} from 'lucide-react'

interface TrustItem {
    name: string
    caption: string
    icon: LucideIcon
    tone: string
    kind: 'department' | 'ecosystem'
}

const ITEMS: Array<TrustItem> = [
    { name: "Computer Science (CSE / IT)", caption: "AI / ML · Full-stack · DSA", icon: Cpu, tone: 'bg-indigo-50 text-indigo-600 ring-indigo-100', kind: 'department' },
    { name: "Electronics & Comm. (ECE)", caption: "IoT · Embedded · VLSI", icon: CircuitBoard, tone: 'bg-fuchsia-50 text-fuchsia-600 ring-fuchsia-100', kind: 'department' },
    { name: "Electrical Engg. (EE)", caption: "Circuits · Power · Renewables", icon: Zap, tone: 'bg-amber-50 text-amber-600 ring-amber-100', kind: 'department' },
    { name: "Mechanical Engg. (ME)", caption: "CAD · Robotics · Design", icon: Cog, tone: 'bg-emerald-50 text-emerald-600 ring-emerald-100', kind: 'department' },
    { name: "Civil & Core", caption: "Sustainable design · Blueprints", icon: Building2, tone: 'bg-orange-50 text-orange-600 ring-orange-100', kind: 'department' },
    { name: "Google for Startups", caption: "Startup & product thinking", icon: Rocket, tone: 'bg-sky-50 text-sky-600 ring-sky-100', kind: 'ecosystem' },
    { name: "Microsoft", caption: "Cloud & developer skill tracks", icon: Cloud, tone: 'bg-blue-50 text-blue-600 ring-blue-100', kind: 'ecosystem' },
    { name: "Skill India", caption: "National skilling framework", icon: Landmark, tone: 'bg-rose-50 text-rose-600 ring-rose-100', kind: 'ecosystem' },
    { name: "NSDC", caption: "Industry-aligned skill standards", icon: BadgeCheck, tone: 'bg-teal-50 text-teal-600 ring-teal-100', kind: 'ecosystem' }
]

const TrustCard: FC<{ item: TrustItem }> = ({ item }): ReactNode => (
    <motion.div
        whileHover={{ y: -4 }}
        className='group flex w-[268px] shrink-0 items-center gap-3.5 rounded-2xl border border-rose-100/80 bg-white px-4 py-3.5 shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition-[border-color,box-shadow] duration-300 hover:border-rose-200 hover:shadow-[0_18px_40px_-20px_rgba(225,29,72,0.35)]'
    >
        <span className={cn('grid size-11 shrink-0 place-items-center rounded-xl ring-1 transition-transform duration-300 group-hover:scale-105', item.tone)}>
            <item.icon className='size-5' />
        </span>

        <span className='min-w-0'>
            <span className='block truncate text-sm font-bold text-ink'>
                {item.name}
            </span>

            <span className='mt-0.5 block truncate text-xs text-slate-500'>
                {item.caption}
            </span>
        </span>
    </motion.div>
)

export const TrustBanner: FC = (): ReactNode => {
    return (
        <section id='departments' aria-labelledby='departments-title' className='bg-white px-4 sm:px-6'>
            <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                className='mx-auto my-12 max-w-6xl overflow-hidden rounded-3xl border border-rose-100/80 bg-blush p-8 sm:p-10'
            >
                <h2 id='departments-title' className='mx-auto max-w-3xl text-balance text-center text-[11px] font-bold leading-relaxed tracking-[0.2em] text-rose-900/70 sm:text-xs'>
                    BUILT FOR ALL 4 GKV FET DEPARTMENTS & ALIGNED WITH LEADING TECH HIRING STANDARDS
                </h2>

                <div className='group/marquee relative mt-8 overflow-hidden fade-mask-edges'>
                    <div className='flex w-max animate-marquee gap-4 py-2 pr-4 group-hover/marquee:[animation-play-state:paused] motion-reduce:w-full motion-reduce:flex-wrap motion-reduce:justify-center'>
                        {ITEMS.map(item => (
                            <TrustCard key={item.name} item={item} />
                        ))}

                        <div aria-hidden='true' className='flex gap-4 motion-reduce:hidden'>
                            {ITEMS.map(item => (
                                <TrustCard key={`${item.name}-copy`} item={item} />
                            ))}
                        </div>
                    </div>
                </div>

                <p className='mt-6 text-center text-xs text-slate-500'>
                    Departments of the Faculty of Engineering & Technology, with skill tracks benchmarked against national and industry standards.
                </p>
            </motion.div>
        </section>
    )
}
